import { useState } from 'react';
import PropTypes from 'prop-types';
import { useNotifications, useUnreadCount, useMarkAsRead, useMarkAllAsRead } from '@hooks';

const notificationIcons = {
  task_assigned: '📋',
  task_updated: '✏️',
  task_completed: '✅',
  mention: '@',
  sprint_started: '🏃',
  sprint_completed: '🎉',
  default: '🔔',
};

function NotificationItem({ notification, onMarkRead }) {
  const icon = notificationIcons[notification.type] || notificationIcons.default;
  const isUnread = !notification.read;

  const timeAgo = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'ahora';
    if (diffMins < 60) return `hace ${diffMins}m`;
    if (diffHours < 24) return `hace ${diffHours}h`;
    if (diffDays < 7) return `hace ${diffDays}d`;
    return notifDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <div
      onClick={() => !notification.read && onMarkRead(notification.id)}
      style={{
        display: 'flex',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: isUnread ? '#f0f4ff' : 'transparent',
        borderBottom: '1px solid #f1f5f9',
        cursor: isUnread ? 'pointer' : 'default',
        transition: 'background-color 0.2s',
      }}
    >
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          backgroundColor: isUnread ? '#667eea15' : '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontSize: '13px',
            fontWeight: isUnread ? '600' : '400',
            color: '#1e293b',
            margin: 0,
            marginBottom: '2px',
          }}
        >
          {notification.title}
        </p>
        <p
          style={{
            fontSize: '12px',
            color: '#64748b',
            margin: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {notification.message}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
        <span style={{ fontSize: '11px', color: '#94a3b8' }}>{timeAgo(notification.createdAt)}</span>
        {isUnread && (
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#667eea',
            }}
          />
        )}
      </div>
    </div>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.object.isRequired,
  onMarkRead: PropTypes.func.isRequired,
};

export default function NotificationPanel({ isOpen, onClose }) {
  const { data: notificationsData, isLoading } = useNotifications({ enabled: isOpen });
  const notifications = notificationsData?.data || [];

  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const handleMarkRead = (id) => {
    markAsRead.mutate(id);
  };

  const handleMarkAllRead = () => {
    markAllAsRead.mutate();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 40,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '8px',
          width: '380px',
          maxHeight: '480px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          border: '1px solid #e2e8f0',
          zIndex: 50,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
            Notificaciones
          </h3>
          <button
            onClick={handleMarkAllRead}
            style={{
              background: 'none',
              border: 'none',
              color: '#667eea',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '6px',
            }}
          >
            Marcar todas leídas
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <p style={{ color: '#94a3b8', fontSize: '14px' }}>Cargando...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔔</div>
              <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
                No tienes notificaciones
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={handleMarkRead}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}

NotificationPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
